import {
  infiniteQueryOptions as baseInfiniteQueryOptions,
  queryOptions as baseQueryOptions,
} from "@tanstack/react-query";

import type { Promisable } from "@core/foundation/promise";
import type {
  OffsetPaginatedResponse,
  OffsetPagination,
} from "@core/ports/http";

// oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
export function offsetPaginatedQueryOptions<Args, Item>({
  queryFn,
  queryKey: { group, variant = () => [] },
  refetchInterval,
}: {
  readonly queryFn: (
    args: Args & OffsetPagination,
  ) => Promisable<OffsetPaginatedResponse<Item>>;
  readonly queryKey: {
    readonly group: (args: Args & OffsetPagination) => readonly unknown[];
    readonly variant?: (args: Args & OffsetPagination) => readonly unknown[];
  };
  readonly refetchInterval?: number;
}) {
  // oxlint-disable-next-line @typescript-eslint/explicit-function-return-type
  const factory = (args: Args & OffsetPagination) => {
    const groupQueryKey = group(args);

    const queryKey = [...groupQueryKey, ...variant(args)] as const;

    const regular = baseQueryOptions({
      queryFn: async () => queryFn(args),
      queryKey,
      refetchInterval,
    });

    const infinite = baseInfiniteQueryOptions({
      getNextPageParam: (
        lastPage: OffsetPaginatedResponse<Item>,
        allPages: readonly OffsetPaginatedResponse<Item>[],
      ) =>
        lastPage.hasMore
          ? allPages.reduce((sum, page) => sum + page.items.length, 0)
          : undefined,
      initialPageParam: 0,
      queryFn: async ({ pageParam }) => queryFn({ ...args, offset: pageParam }),
      queryKey: [...queryKey, "infinite"],
    });

    return Object.assign(regular, { groupQueryKey, infinite });
  };

  return factory;
}
